import { useState } from "react";
import { ReadonlyBlock } from "@/app/ReadonlyBlock";
import type { Attempt } from "@/app/messages";
import { Alert, Button, EmptyState, Grid, KeyValue, SectionTitle } from "@/ui";
import { isTraceObject, storedRoutingTrace, traceIdentity, traceIDs, traceNumber, traceReason } from "./routingTraceView";

/** 逐次展示定格路由；重试不借用首发的账号/模板命中，历史名称不查现有配置。 */
export function MessageRoutingTrace({ attempts, failureTrace }: { attempts: Attempt[]; failureTrace?: unknown }) {
  return <section className="ax-section"><SectionTitle>路由决策</SectionTitle>
    <p className="ax-secondary">按当时记录的身份、版本与顺序展示。数字越小越优先，账号与模板优先级分别比较；路由配置保存即生效，只影响之后的首发，已定格的尝试不重新匹配。</p>
    {!attempts.length && failureTrace == null ? <EmptyState title="没有持久化路由记录">未记录首发失败诊断或尝试路由；仅凭客户错误码不能推断匹配结果。</EmptyState> :
      <Grid>
        {failureTrace != null && <TraceRecord record={failureTrace} title="首发失败 · 未创建提交" label="首发失败" />}
        {[...attempts].sort((a, b) => a.seq_no - b.seq_no).map(a => <TraceRecord key={a.id} record={a.route_trace} title={`第 ${a.seq_no} 次尝试 · 通道 #${a.channel_id}`} label={`第 ${a.seq_no} 次`} />)}
      </Grid>}
  </section>;
}

// 消息级首发失败与实际尝试共用历史记录展示，不把失败诊断伪造成attempt（D128）。
function TraceRecord({ record, title, label }: { record: unknown; title: string; label: string }) {
  const [expanded, setExpanded] = useState(false), [raw, setRaw] = useState(false);
  const t = storedRoutingTrace(record);
  const account = t && isTraceObject(t.account_routing) ? t.account_routing : null;
  const items = account ? [
    { label: "配置快照版本", value: traceNumber(account.config_version) }, { label: "账号路由版本", value: traceNumber(account.account_version) },
    { label: "账号绑定", value: traceIdentity(account.binding_id) }, { label: "账号优先级", value: traceIdentity(account.binding_id) === "—" ? "—" : traceNumber(account.priority) },
    { label: "账号规则", value: traceIdentity(account.rule_id, account.rule_version) }, { label: "匹配结果", value: traceReason(account.reason) },
    ...(typeof account.template_id === "number" && account.template_id > 0 ? [
      { label: "继承模板", value: traceIdentity(account.template_id, account.template_version) },
      { label: "模板明细绑定", value: traceIdentity(account.template_binding_id) }, { label: "模板优先级", value: traceIdentity(account.template_binding_id) === "—" ? "—" : traceNumber(account.template_priority) },
      { label: "模板明细规则", value: traceIdentity(account.template_rule_id, account.template_rule_version) },
    ] : []),
    // 命中前因模板停用或未命中而回退的模板绑定，按尝试顺序列出（D132）。
    ...(Array.isArray(account.skipped_templates) ? account.skipped_templates.filter(isTraceObject).map((sk, i) => ({
      label: `跳过模板 ${i + 1}`, value: `绑定 ${traceIdentity(sk.binding_id)}（优先级 ${traceNumber(sk.priority)}）· 模板 ${traceIdentity(sk.template_id, sk.template_version)} · ${traceReason(sk.reason)}`,
    })) : []),
  ] : t ? [
    { label: "旧路由方案", value: traceIdentity(t.route_id, t.route_version) }, { label: "旧命中明细", value: traceIDs(t.matched_rule_ids) },
  ] : [];
  const excluded = t && Array.isArray(t.excluded) ? t.excluded.filter(isTraceObject) : [];
  const plan = isTraceObject(record) && Array.isArray(record.plan) ? record.plan.filter(isTraceObject) : [];
  return <div className="ax-fieldset"><Grid>
    <div className="ax-row"><span>{title}</span><Button size="sm" onClick={() => setExpanded(!expanded)}>{expanded ? "收起" : "查看"}{label}路由</Button></div>
    {expanded && <>
      {!t ? <Alert tone="neutral" title="路由记录缺失或格式未知">不从当前配置推断历史命中；可以查看原始记录。</Alert> : <>
        {!account && <p className="ax-secondary">未记录账号两层匹配。旧首发按旧方案字段解释；重试沿用定格目标，不重新匹配账号或模板。</p>}
        <KeyValue items={items.map(item => ({ ...item, mono: true }))} />
        <KeyValue items={[
          { label: "重试通道组合", value: traceIdentity(t.retry_group_id), mono: true }, { label: "重试通道", value: traceIdentity(t.retry_channel_id), mono: true },
          { label: "通道组合路径", value: traceIDs(t.group_path), mono: true }, { label: "策略回退说明", value: typeof t.fallback === "string" && t.fallback ? t.fallback : "—" },
        ]} />
        {!!excluded.length && <KeyValue items={excluded.map(e => ({ label: `剔除通道 ${traceIdentity(e.channel_id)}`, value: traceReason(e.reason) }))} />}
        {!!plan.length && <KeyValue items={plan.map((p, i) => ({ label: `计划第 ${i + 1} 项`, value: `${traceIdentity(p.channel_id)}；分数 ${traceNumber(p.score)}；${traceReason(p.reason)}`, mono: true }))} />}
      </>}
      <Button size="sm" variant="ghost" onClick={() => setRaw(!raw)}>{raw ? "收起" : "查看"}{label}原始记录</Button>
      {raw && <ReadonlyBlock label={`${label}路由原始JSON`} rows={8} value={JSON.stringify(record ?? null, null, 2)} />}
    </>}
  </Grid></div>;
}
